import { Component, lazy, Suspense, useState, type ErrorInfo, type ReactNode } from 'react'
import { toast } from 'sonner'
import { Loader2 } from 'lucide-react'
import { useIsMobile } from '@/hooks/useMediaQuery'
import { isChunkLoadError, reloadForUpdate } from '@/lib/appUpdate'
import { reportError } from '@/lib/monitoring'
import { AssistantAside } from './AssistantAside'
import { useAssistant } from './AssistantProvider'

const loadPanel = () => import('./AssistantPanel')

const LazyAssistantPanel = lazy(loadPanel)

let preloaded = false

export function preloadAssistantPanel() {
  if (preloaded) return
  preloaded = true
  loadPanel().catch(() => {
    preloaded = false
  })
}

class AssistantBoundary extends Component<{ children: ReactNode; resetKey: string; onFail: () => void }, { failed: boolean }> {
  state = { failed: false }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.props.onFail()
    if (isChunkLoadError(error) && reloadForUpdate()) return
    reportError(error, info.componentStack)
    toast.error('Não foi possível abrir o assistente. Tente de novo em instantes.')
  }

  componentDidUpdate(previous: { resetKey: string }) {
    if (this.state.failed && previous.resetKey !== this.props.resetKey) this.setState({ failed: false })
  }

  render() {
    return this.state.failed ? null : this.props.children
  }
}

function PanelFallback() {
  const isMobile = useIsMobile()
  if (isMobile) return null
  return (
    <AssistantAside aria-busy>
      <div
        className="flex flex-1 items-center justify-center rounded-xl border border-sidebar-border bg-background shadow-sm"
        role="status"
        aria-label="Abrindo o assistente"
      >
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    </AssistantAside>
  )
}

export function AssistantPanelSlot() {
  const { enabled, open, setOpen } = useAssistant()
  const [requested, setRequested] = useState(open)
  if (open && !requested) setRequested(true)
  if (!enabled || !requested) return null
  return (
    <AssistantBoundary resetKey={String(open)} onFail={() => setOpen(false)}>
      <Suspense fallback={open ? <PanelFallback /> : null}>
        <LazyAssistantPanel />
      </Suspense>
    </AssistantBoundary>
  )
}
